<template>
  <div>
    <div :class="$style.actions">
      <badge-indicator-filter
        v-if="false"
        v-model="brandTypes"
        :items="$options.brandTypeBadgesFilterItems"
        :class="$style.filter"
      />
      <date-picker
        :class="$style.filter"
        :date-range="dateRange"
        :min-date="minDate"
        :max-date="maxDate"
        opens="center"
        @update="dateRange = $event"
      />
      <div :class="$style['date-group-holder']">
        <select-input
          v-model="dateGroupValue"
          :options="dateGroupOptions"
          :placeholder="$t('itemization')"
          :searchable="false"
          bind-value-id
        />
      </div>
      <div :class="$style['next-to-last']">
        <badge-indicator-filter
          v-model="_devices"
          :items="deviceBadgesFilterItems"
          :class="$style.filter"
        />
      </div>
      <btn
        secondary
        icon="settings"
        slim
        @click="isModalOpened = true; brandQueriesText = null"
      >
        {{ $t('title.brand-requests') }}
      </btn>
    </div>
    <data-grid
      :data="rows"
      :columns="columns"
      :height="`calc(100vh - 222px)`"
      :auto-group-column-def="groupColumnDef"
      :register-components="{ SegmentCell }"
      :loading="isLoading"
      :stripped="false"
      :get-row-style="getRowStyle"
      tree
    />
    <btn
      :class="$style.button"
      @click="isSegmentModalOpened = true; segment = undefined"
    >
      {{ $t('action.add.segment') }}
    </btn>
    <modal
      v-model="isSegmentModalOpened"
      :title="segmentModalTitle"
      :primary-button-label="segmentModalLabel"
      :disabled="isSegmentModalDisabled"
      :loading="segmentCreateRequest.loading || segmentUpdateRequest.loading || segmentDeleteRequest.loading"
      @ok="handleSegmentSave"
    >
      <segment-form
        v-model="segment"
        @delete="handleSegmentDelete"
      />
    </modal>
    <modal
      v-model="isModalOpened"
      :title="$t('title.brand-requests')"
      :primary-button-label="$t('action.change')"
      :loading="isLoading"
      @ok="handleOk"
    >
      <text-input
        v-model="brandQueries"
        multiline
        :rows="7"
      />
      <div :class="$style['modal-hint']">
        {{ $t('hint.brand-queries-format') }}
      </div>
    </modal>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import { groupByKeys } from "@/api/traffic";
import { stringToVariant } from "@/utils/stringToVariant";
import { SEGMENT, SEGMENT_ENTITY } from '@/store';
import Traffic from "@/modules/traffic/pages/Traffic";
import BadgeIndicatorFilter from '@/ui-kit/BadgeIndicatorFilter';
import Btn from '@/ui-kit/Btn';
import DataGrid from '@/ui-kit/DataGrid/DataGrid';
import SelectInput from '@/ui-kit/SelectInput';
import { brandTypeKeys, brandTypeBadgesFilterItems } from "../constants";
import { convertSegmentsToRows } from '../utils';
import { getSegmentColumns } from './columns/columns';
import SegmentCell from './columns/SegmentCell/SegmentCell';
import { eventBus } from './columns/SegmentCell/event-bus';
import SegmentForm from '../components/SegmentForm';

const { GETTER, MUTATION, ACTION } = SEGMENT;

const { segment } = groupByKeys;

export default {
  groupBy: segment,
  brandTypeKeys,
  brandTypeBadgesFilterItems,
  components: {
    BadgeIndicatorFilter,
    Btn,
    SelectInput,
    DataGrid,
    SegmentForm,
  },
  extends: Traffic,
  data() {
    return {
      segment: undefined,
      isSegmentModalOpened: false,
      groupColumnDef: {
        pinned: 'left',
        headerName: this.$t('segment'),
        minWidth: 300,
        cellRendererParams: { suppressCount: true },
        resizable: true,
        valueGetter: ({ data }) => {
          return data?.group === 'segment' ? data.segment.name : this.$t(`traffic.${data.group || data.type}`);
        },
        cellStyle({ data }) {
          const result = {};
          if (data.group === 'segment') {
            result.fontWeight = '700';
          }
          if (data.type) {
            result.color = '#8391a2';
          }
          return result;
        },
        cellRenderer: 'SegmentCell',
      },
      SegmentCell,
    };
  },
  computed: {
    ...mapGetters({
      dateGroupItems: GETTER.DATE_GROUP_LIST,
      dateGroup: GETTER.DATE_GROUP,
      filters: GETTER.FILTERS,
      devices: GETTER.DEVICES,
      error: GETTER.ERROR,
      isLoading: GETTER.IS_LOADING,
      tableView: GETTER.TABLE_VIEW_SEGMENT,
      selectDateGroup: GETTER.DATE_GROUP,
      minDate: GETTER.MIN_DATE,
      maxDate: GETTER.MAX_DATE,
      showTrafficGraph: GETTER.LINECHARTS_SHOW,
      segmentCreateRequest: SEGMENT_ENTITY.GETTER.CREATE_REQUEST,
      segmentUpdateRequest: SEGMENT_ENTITY.GETTER.UPDATE_REQUEST,
      segmentDeleteRequest: SEGMENT_ENTITY.GETTER.DELETE_REQUEST,
    }),
    dateGroupOptions() {
      return this.dateGroupItems.map(({ id }) => ({
        id,
        title: this.$t(`date.${id}`),
      }));
    },
    brandTypes: {
      get() {
        return this.$store.getters[GETTER.BRAND_TYPES];
      },
      set(value) {
        this.setBrandTypes(value);
      },
    },
    rows() {
      return this.tableView?.data ? convertSegmentsToRows(this.tableView.data) : [];
    },
    columns() {
      return getSegmentColumns(this.tableView?.data ?? [], this.$formatDate);
    },
    dateGroupValue: {
      get() {
        return this.dateGroup;
      },
      set(value) {
        this.setDateGroup(value);
      }
    },
    segmentModalTitle() {
      return this.$t(`title.${this.segment?.id ? 'edit' : 'create-new'}-segment`);
    },
    segmentModalLabel() {
      return this.$t(`action.${this.segment?.id ? 'change' : 'create'}`);
    },
    isSegmentModalDisabled() {
      return !this.segment?.name || !this.segment?.regex;
    },
  },
  watch: {
    segmentUpdateRequest({ data, loading }) {
      if (!loading && data) {
        this.isSegmentModalOpened = false;
        this.getData();
      }
    },
    segmentCreateRequest({ data, loading }) {
      if (!loading && data.id) {
        this.isSegmentModalOpened = false;
        this.getData();
      }
    },
    segmentDeleteRequest({ data, loading }) {
      if (!loading && data === 'OK') {
        this.isSegmentModalOpened = false;
        this.getData();
      }
    },
  },
  mounted() {
    eventBus.$on('settings-click', segment => {
      this.isSegmentModalOpened = true;
      this.segment = { ...segment };
    });
  },
  methods: {
    stringToVariant,
    ...mapActions({
      _getData: ACTION.GET_LIST,
      setFilters: ACTION.UPDATE_FILTERS,
      setDateGroup: ACTION.SET_DATE_GROUP,
      setDiagram: ACTION.SET_DIAGRAMM,
      setShowTrafficGraph: ACTION.SET_LINECHARTS_SHOW,
      updateSegment: SEGMENT_ENTITY.ACTION.UPDATE,
      createSegment: SEGMENT_ENTITY.ACTION.CREATE,
      deleteSegment: SEGMENT_ENTITY.ACTION.DELETE,
    }),
    ...mapMutations({
      setBrandTypes: MUTATION.SET_BRAND_TYPES,
      resetAll: MUTATION.RESET,
    }),
    getData() {
      this._getData({ groupBy: segment });
    },
    getRowStyle({ data }) {
      if (data.group === 'segment') {
        return { cursor: 'pointer' };
      }
      return data.group === 'yandex' || data.group === 'google' ? { background: '#F1F2F3' } : {};
    },
    handleSegmentSave() {
      const method = this.segment?.id ? this.updateSegment : this.createSegment;
      method({
        ...this.segment,
        project_id: this.projectId.id,
      });
    },
    async handleSegmentDelete() {
      const confirm = await this.$confirm(
          this.$t('confirm.segment-delete', { segment: this.segment.name }),
          { delete: true },
      );
      if (confirm) {
        this.deleteSegment(this.segment.id);
      }
    },
  },
};
</script>

<style lang="scss" module>
@import '~@/ui-kit/_theme/main.scss';

.actions {
  display: flex;
  align-items: center;
  flex-flow: row nowrap;
  margin-bottom: 16px;
}

.filter {
  margin-right: 16px;
}

.indexRowCell {
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
}

.indexRowCellIcon {
  cursor: pointer;
}

.segment {
  margin-right: 16px;
  font-size: 0.9em;
}

.date-group-holder {
  width: 135px;
  margin-right: $gap-s;
}

.next-to-last {
  flex-grow: 1;
}

.settings {
  position: relative;
  top: 3px;
}

.button {
  margin-top: $gap-s;
  position: absolute;
}
</style>
